import { z } from "zod";

export const SUPPORT_SCREENSHOT_LIMIT = 3;
export const SUPPORT_SCREENSHOT_BYTES = 512 * 1024;
const maxEncodedLength = 4 * Math.ceil(SUPPORT_SCREENSHOT_BYTES / 3);

// Inline raster images only. Never accept public URLs, SVG, HTML or unbounded images.
export function isSupportImage(value: string): boolean {
  if (value.length > maxEncodedLength + 32) return false;
  const match = /^data:image\/(png|jpeg);base64,([A-Za-z0-9+/]+={0,2})$/.exec(value);
  if (!match || match[2]!.length % 4 !== 0) return false;
  try {
    const bytes = Uint8Array.from(atob(match[2]!), char => char.charCodeAt(0));
    if (bytes.length > SUPPORT_SCREENSHOT_BYTES) return false;
    const dimension = (width: number, height: number) => width > 0 && height > 0 && width <= 4096 && height <= 4096;
    if (match[1] === "png") {
      const view = new DataView(bytes.buffer);
      return bytes.length >= 45 && [137, 80, 78, 71, 13, 10, 26, 10].every((byte, i) => bytes[i] === byte)
        && view.getUint32(8) === 13 && view.getUint32(12) === 0x49484452
        && view.getUint32(bytes.length - 8) === 0x49454e44 && dimension(view.getUint32(16), view.getUint32(20));
    }
    if (bytes[0] !== 255 || bytes[1] !== 216 || bytes.at(-2) !== 255 || bytes.at(-1) !== 217) return false;
    for (let i = 2; i + 8 < bytes.length;) {
      if (bytes[i++] !== 255) return false;
      while (bytes[i] === 255) i++;
      const marker = bytes[i++]!;
      const length = (bytes[i]! << 8) + bytes[i + 1]!;
      if (length < 2 || i + length > bytes.length) return false;
      if ([0xc0, 0xc1, 0xc2].includes(marker)) return dimension((bytes[i + 5]! << 8) + bytes[i + 6]!, (bytes[i + 3]! << 8) + bytes[i + 4]!);
      if (marker === 0xda || marker === 0xd9) return false;
      i += length;
    }
  } catch { return false; }
  return false;
}

export const SupportImageDataSchema = z.string().max(maxEncodedLength + 32).refine(isSupportImage, "截图格式无效或超过 512 KB，请重新上传");
export const HumanSupportScreenshotSchema = z.object({
  id: z.string().uuid(),
  source: z.enum(["page", "attachment", "upload"]),
  addedAt: z.string().datetime(),
  pageUrl: z.string().url().max(1200).optional(),
  // List responses contain metadata only; authenticated detail responses contain the image.
  dataUrl: SupportImageDataSchema.optional(),
});
export type HumanSupportScreenshot = z.infer<typeof HumanSupportScreenshotSchema>;
